"use client";

import { useEffect } from "react";

import { reportError } from "@/lib/report-error";

/**
 * Global error boundary — the last line of defense (catches errors in the root
 * layout itself). It replaces the whole document, so it ships its own
 * <html>/<body> and cannot rely on app styles/providers. Keep it self-contained.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError(error, { source: "boundary:global", digest: error.digest });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, sans-serif",
          background: "#030712",
          color: "#f9fafb",
        }}
      >
        <div
          style={{ maxWidth: "32rem", padding: "2rem", textAlign: "center" }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 600 }}>
            Something went wrong
          </h1>
          <p style={{ color: "#9ca3af", marginTop: "0.5rem" }}>
            The application hit an unexpected error and needs to reload.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              marginTop: "1.5rem",
              padding: "0.5rem 1.25rem",
              /* .7rem = the unified control radius (`rounded-lg`). Literal,
               * for the same reason as the colours below: no globals.css. */
              borderRadius: "0.7rem",
              border: "none",
              /* Literal hex, not `hsl(var(--primary))`: global-error renders
               * its own <html>, so globals.css and its tokens are not
               * guaranteed to have loaded. Values mirror --primary /
               * --primary-foreground (Polymarket --blue-400, white at 7.14:1)
               * — the solid-CTA ramp, not the sky --nav-active #0093fd. */
              background: "#164bcf",
              color: "#ffffff",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      </body>
    </html>
  );
}
